import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Project, V3Event, V3Room, V3User } from "../types/v3types";
import styled from "styled-components";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PlayerUrlSection = styled.div`
  background: #e3f2fd;
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const UrlLabel = styled.span`
  font-weight: bold;
  color: #1976d2;
`;

const UrlText = styled.span`
  font-family: monospace;
  background: white;
  padding: 8px 12px;
  border-radius: 4px;
  flex: 1;
`;

const CopyButton = styled.button`
  padding: 8px 16px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    background-color: #1565c0;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`;

const ProjectInfo = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: bold;
  min-width: 120px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #0056b3;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background-color: ${props => {
    switch (props.status) {
      case "waiting": return "#ffc107";
      case "running": return "#28a745";
      case "finished": return "#6c757d";
      case "active": return "#28a745";
      case "inactive": return "#dc3545";
      default: return "#6c757d";
    }
  }};
  color: white;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
`;

const AdminProjectDashboard: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<V3Project | null>(null);
  const [events, setEvents] = useState<V3Event[]>([]);
  const [rooms, setRooms] = useState<V3Room[]>([]);
  const [users, setUsers] = useState<V3User[]>([]);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  const playerUrl = `${window.location.origin}/v3/${projectId}/start`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(playerUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  useEffect(() => {
    if (!projectId) return;

    // Load project data
    const loadProject = async () => {
      try {
        const projectDoc = await getDoc(doc(db, "v3projects", projectId));
        if (projectDoc.exists()) {
          setProject(projectDoc.data() as V3Project);
        } else {
          navigate("/admin");
        }
      } catch (error) {
        console.error("Error loading project:", error);
      }
    };

    loadProject();

    // Subscribe to events
    const eventsQuery = query(
      collection(db, "v3events"),
      where("projectId", "==", projectId),
      orderBy("createdAt", "desc")
    );
    const unsubscribeEvents = onSnapshot(eventsQuery, (snapshot) => {
      const eventData = snapshot.docs.map(doc => ({ ...doc.data(), eventId: doc.id } as V3Event));
      setEvents(eventData);
    });

    // Subscribe to rooms
    const roomsQuery = query(
      collection(db, "v3rooms"),
      where("projectId", "==", projectId),
      orderBy("createdAt", "desc")
    );
    const unsubscribeRooms = onSnapshot(roomsQuery, (snapshot) => {
      const roomData = snapshot.docs.map(doc => ({ ...doc.data(), roomId: doc.id } as V3Room));
      setRooms(roomData);
    });

    // Subscribe to users
    const usersQuery = query(
      collection(db, "v3users"),
      where("projectId", "==", projectId),
      orderBy("lastActiveAt", "desc")
    );
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const userData = snapshot.docs.map(doc => ({ ...doc.data(), userId: doc.id } as V3User));
      setUsers(userData);
      setLoading(false);
    });

    return () => {
      unsubscribeEvents();
      unsubscribeRooms();
      unsubscribeUsers();
    };
  }, [projectId, navigate]);

  if (loading || !project) {
    return <Container>読み込み中...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>{project.projectName}</Title>
        <Button onClick={() => navigate("/admin")}>プロジェクト一覧へ</Button>
      </Header>

      <PlayerUrlSection>
        <UrlLabel>プレイヤー用URL:</UrlLabel>
        <UrlText>{playerUrl}</UrlText>
        <CopyButton onClick={copyToClipboard}>
          {copySuccess ? "✓ コピーしました" : "📋 コピー"}
        </CopyButton>
      </PlayerUrlSection>

      <ProjectInfo>
        <InfoRow>
          <InfoLabel>プロジェクトID:</InfoLabel>
          <span>{project.projectId}</span>
        </InfoRow>
        <InfoRow>
          <InfoLabel>作成日時:</InfoLabel>
          <span>{project.createdAt?.toDate?.().toLocaleString() || "不明"}</span>
        </InfoRow>
        <InfoRow>
          <InfoLabel>ステータス:</InfoLabel>
          <StatusBadge status={project.isActive ? "active" : "inactive"}>
            {project.isActive ? "アクティブ" : "非アクティブ"}
          </StatusBadge>
        </InfoRow>
        {project.description && (
          <InfoRow>
            <InfoLabel>説明:</InfoLabel>
            <span>{project.description}</span>
          </InfoRow>
        )}
      </ProjectInfo>

      <Section>
        <SectionTitle>
          イベント一覧 ({events.length})
          <Button onClick={() => navigate(`/v3/${projectId}/events/new`)}>
            新規イベント作成
          </Button>
        </SectionTitle>
        {events.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>イベント名</Th>
                <Th>作成者</Th>
                <Th>問題数</Th>
                <Th>ステータス</Th>
                <Th>作成日時</Th>
                <Th>操作</Th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.eventId}>
                  <Td>{event.eventName}</Td>
                  <Td>{event.creatorId}</Td>
                  <Td>{event.problems?.length || 0}</Td>
                  <Td>
                    <StatusBadge status={event.isActive ? "active" : "inactive"}>
                      {event.isActive ? "公開" : "非公開"}
                    </StatusBadge>
                  </Td>
                  <Td>{event.createdAt?.toDate?.().toLocaleString()}</Td>
                  <Td>
                    <Button onClick={() => navigate(`/admin/v3/projects/${projectId}/events/${event.eventId}`)}>
                      編集
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>イベントがありません</EmptyState>
        )}
      </Section>

      <Section>
        <SectionTitle>
          開催中のルーム ({rooms.filter(r => r.status === "running").length})
          <Button onClick={() => navigate(`/admin/v3/projects/${projectId}/rooms/new`)}>
            新規ルーム作成
          </Button>
        </SectionTitle>
        {rooms.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>ルーム名</Th>
                <Th>イベント</Th>
                <Th>参加者数</Th>
                <Th>ステータス</Th>
                <Th>開始時刻</Th>
                <Th>操作</Th>
              </tr>
            </thead>
            <tbody>
              {rooms.map(room => (
                <tr key={room.roomId}>
                  <Td>{room.roomName}</Td>
                  <Td>{events.find(e => e.eventId === room.eventId)?.eventName || room.eventId}</Td>
                  <Td>{room.participants?.length || 0}</Td>
                  <Td>
                    <StatusBadge status={room.status}>
                      {room.status === "waiting" && "待機中"}
                      {room.status === "running" && "開催中"}
                      {room.status === "finished" && "終了"}
                      {room.status === "cancelled" && "中止"}
                    </StatusBadge>
                  </Td>
                  <Td>{room.startTime?.toDate?.().toLocaleString() || "-"}</Td>
                  <Td>
                    <Button onClick={() => navigate(`/admin/v3/projects/${projectId}/rooms/${room.roomId}`)}>
                      管理
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>ルームがありません</EmptyState>
        )}
      </Section>

      <Section>
        <SectionTitle>
          参加者一覧 ({users.length})
        </SectionTitle>
        {users.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>ユーザー名</Th>
                <Th>ロール</Th>
                <Th>作成イベント数</Th>
                <Th>参加イベント数</Th>
                <Th>最終アクティブ</Th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.userId}>
                  <Td>{user.username}</Td>
                  <Td>{user.role}</Td>
                  <Td>{user.stats?.eventsCreated || 0}</Td>
                  <Td>{user.stats?.eventsParticipated || 0}</Td>
                  <Td>{user.lastActiveAt?.toDate?.().toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>参加者がいません</EmptyState>
        )}
      </Section>
    </Container>
  );
};

export default AdminProjectDashboard;